<template>
  <div class="container-fluid page-container">
    <div v-if="isChild">
      <router-view />
    </div>
    <div v-else>
      <div class="row page-header__container mb-4 pb-3">
        <page-heading :class="'col col-md-4 align-items-center'" :title="pageTitle" />

        <div class="col d-flex flex-column flex-xl-row align-items-center justify-content-end filter">
          <invoices-list-control
            :class="'col justify-content-end pr-md-0'"
            :sortFields="(isMobile) ? tableFields : null"
            @sort="onSort"
          >
            <template v-if="isDesktop" #contextual>
              <div class="fake-select separator manage-payment d-flex">
                <router-link class="mr-2 text-link  align-self-center" :to="{name: 'payment-methods'}">
                  {{ $t('Manage Payment Methods') }}
                </router-link>
              </div>
              <div v-b-tooltip.hover class="fake-select separator settle-account" :title="accountBalance === 0 ? 'Your account is settled. Thank you.' : ''">
                <b-button variant="outline-secondary" :disabled="accountBalance === 0" size="sm" class="mr-1" @click="onSettleAccount">
                  {{ $t('Settle Account') }}
                </b-button>
              </div>
              <div class="fake-select separator export-invoices pr-md-0">
                <b-button variant="outline-secondary" size="sm" class="mr-1 " @click="selectedPane = 'export'">
                  {{ $t('Export Invoices') }}
                </b-button>
              </div>
            </template>
            <template v-else #contextual>
              <div v-if="isTablet" class="text-left list-control__mobile__pane__item select-accordion">
                <div class="select-accordion__button d-flex justify-content-between">
                  <div v-b-tooltip.hover
                       :title="accountBalance === 0 ? $t('Your account is settled. Thank you.') : ''"
                       class="mr-1 text-nowrap font-weight-bold"
                  >
                    <b-button
                      class="p-0 btn-empty align-self-center font-weight-bold text-primary btn-settle-account"
                      :disabled="accountBalance === 0"
                      @click="onSettleAccount"
                    >
                      {{ $t('Settle Account') }}
                    </b-button>
                  </div>
                </div>
              </div>
              <div class="text-left list-control__mobile__pane__item select-accordion">
                <div class="select-accordion__button d-flex justify-content-between">
                  <div class="mr-1 text-nowrap font-weight-bold">
                    <router-link
                      class="p-0 btn-empty align-self-center"
                      :to="{name: 'payment-methods'}"
                    >
                      {{ $t('Manage Payment Methods') }}
                    </router-link>
                  </div>
                </div>
              </div>
              <div class="text-left list-control__mobile__pane__item select-accordion">
                <div class="select-accordion__button d-flex justify-content-between">
                  <div class="mr-1 text-nowrap font-weight-bold">
                    <button
                      class="p-0 d-flex btn-empty font-weight-bold text-primary"
                      @click="selectedPane = 'export'"
                    >
                      {{ $t('Export Invoices') }}
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </invoices-list-control>
        </div>
      </div>

      <div
        v-if="displaySettleAccount"
        class="row pb-3 position-sticky settle-account"
      >
        <div class="col-12 col-md-8 m-auto">
          <div class="card py-3 px-2 shadow rounded border bg-white">
            <div
              v-if="selectedPane === null"
              v-b-tooltip.hover
              class="col-12 flex-row d-flex align-items-center account-balance justify-content-between"
              :title="accountBalance === 0 ? $t('Your account is settled. Thank you.') : ''"
            >
              <div class="account-balance-details">
                <span class="mr-2 h4 mb-0">Your balance:</span>
                <span class="font-weight-bold h4 mb-0">{{ $n(accountBalance, 'currency') }} </span>
                <small class="vat-text"> + VAT</small>
              </div>
              <b-button
                v-if="selectedInvoices.length === 0 && (selectedPane !== 'payment')"
                variant="outline-secondary"
                size="sm"
                class="settle-invoice mr-1"
                @click="onSettleAccount"
              >
                {{ $t('Settle Account') }}
              </b-button>
              <b-button
                v-if="selectedInvoices.length > 0 && (selectedPane !== 'payment')"
                class="settle-invoice pay-invoice mr-1" variant="primary" size="sm"
                @click="selectedPane = 'payment'"
              >
                {{ $t('Pay') }}
              </b-button>
            </div>

            <div
              v-if="selectedInvoices.length > 0 && (selectedPane !== 'payment')"
              class="col-12 mt-2"
            >
              <hr class="w-75">
              <div class="row bg-white align-items-center">
                <div class="col-3">
                  <b-button class="p-0" variant="link" @click="onPaymentCancel">
                    {{ $t('Cancel') }}
                  </b-button>
                </div>
                <div class="col-9">
                  <p class="mb-0">
                    {{ $tc('invoicesSelected', selectedInvoices.length, {count: selectedInvoices.length}) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div :class="['col', 'col-12', 'order-md-0', 'order-2', invoicesContainerClass]">
          <my-invoices-mobile-list
            v-if="isMobile"
            ref="invoiceList"
            v-model="invoiceToView"
            :sort="sortField"
            :show="selectedPane == null"
            :hasInvoicesSelected="selectedInvoices.length > 0"
            @invoices-selected="onInvoicesSelected"
            @invoices-loaded="onInvoicesLoaded"
            @show-payment="showPayment = true"
          />
          <my-invoices-table
            v-else
            ref="invoiceTable"
            v-model="invoiceToView"
            :compact="compactTable"
            :fields="tableFields"
            :sort="sortField"
            @invoices-selected="onInvoicesSelected"
            @invoices-loaded="onInvoicesLoaded"
          />
        </div>

        <div v-if="selectedPane === 'payment'" :class="['col', 'col-12', 'col-xl-4', 'order-1', 'order-md-1', paneClass]">
          <transition-expand-side :expand-direction="'expand-side-left'">
            <b-card
              class="d-block border shadow sticky-basket"
              :header-class="'rounded-top'"
              :body-class="['rounded-bottom', 'overflow-hidden']"
            >
              <template #header>
                <b-col class="col-12 px-0 d-flex align-items-center">
                  <b-button class="close position-absolute" variant="link" @click="onPaymentCancel">
                    <font-awesome-icon icon="times" />
                  </b-button>
                  <h2 class="text-center m-auto h4">
                    {{ $t('Pay Invoice') }}
                  </h2>
                </b-col>
              </template>
              <invoice-payment :invoices="selectedInvoices" @complete="onPaymentComplete" @invoice-removed="onInvoiceRemoved" />
            </b-card>
          </transition-expand-side>
        </div>

        <div v-else-if="selectedPane === 'export'" :class="['col', 'col-12', 'col-xl-4', 'order-1', 'order-md-1', paneClass]">
          <transition-expand-side :expand-direction="'expand-side-left'">
            <b-card
              class="d-block border shadow sticky-basket"
              :body-class="'p-4 rounded shadow'"
            >
              <template #header>
                <b-button class="close position-absolute" variant="link" @click="selectedPane = null">
                  <font-awesome-icon icon="times" />
                </b-button>
                <b-col class="col-12 px-0 d-flex align-items-center">
                  <h2 class="text-center m-auto h4">
                    {{ $t('Export Invoices') }}
                  </h2>
                </b-col>
              </template>
              <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-12 col-xxl-10">
                  <div class="row">
                    <div class="col-12 pt-4">
                      <b-form-select v-model="exportSelection" class="custom-select-sm">
                        <b-form-select-option value="today">
                          {{ $t('Today') }}
                        </b-form-select-option>
                        <b-form-select-option value="yesterday">
                          {{ $t('Yesterday') }}
                        </b-form-select-option>
                        <b-form-select-option value="thisWeek">
                          {{ $t('This Week') }}
                        </b-form-select-option>
                        <b-form-select-option value="thisMonth">
                          {{ $t('This Month') }}
                        </b-form-select-option>
                        <b-form-select-option value="lastMonth">
                          {{ $t('Last Month') }}
                        </b-form-select-option>
                        <b-form-select-option value="quarter">
                          {{ $t('Quarter to Date') }}
                        </b-form-select-option>
                        <b-form-select-option value="year">
                          {{ $t('Year to Date') }}
                        </b-form-select-option>
                        <b-form-select-option value="last12">
                          {{ $t('Last 12 Months') }}
                        </b-form-select-option>
                        <b-form-select-option value="all">
                          {{ $t('All Time') }}
                        </b-form-select-option>
                        <b-form-select-option value="custom">
                          {{ $t('Custom') }}
                        </b-form-select-option>
                      </b-form-select>
                    </div>

                    <div class="col-12 pt-4">
                      <label class="pb-0 col-form-label-sm">{{ $t('Start Date') }}</label>
                      <b-form-datepicker
                        v-model="exportStart"
                        size="sm"
                        :min="minExportDate"
                        :max="exportEnd"
                        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' }"
                      />
                    </div>

                    <div class="col-12 pt-4">
                      <label class="pb-0 col-form-label-sm">{{ $t('End Date') }}</label>
                      <b-form-datepicker
                        v-model="exportEnd"
                        size="sm"
                        :min="exportStart"
                        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' }"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-12 align-content-center text-center mt-3 pt-5">
                  <div class="d-inline-block">
                    <b-button variant="link" @click="onExportCancel">
                      {{ $t('Cancel') }}
                    </b-button>
                    <a :href="exportUrl" class="btn btn-sm btn-secondary">{{ $t('Download .CSV') }}</a>
                  </div>
                </div>
              </div>
            </b-card>
          </transition-expand-side>
        </div>

        <div v-else-if="selectedPane === 'invoice'" :class="['col', 'col-12', 'col-xl-4', 'order-1', 'order-md-1', paneClass]">
          <transition-expand-side :expand-direction="'expand-side-left'">
            <b-card
              class="d-block border shadow sticky-basket"
              :header-class="'rounded-top'"
              :body-class="['rounded-bottom', 'overflow-hidden', 'pdf-preview-background']"
            >
              <template #header>
                <b-col class="col px-0 d-flex align-items-center">
                  <b-button class="close position-absolute" variant="link" @click="closeInvoice">
                    <font-awesome-icon icon="times" />
                  </b-button>
                  <div class="col d-flex flex-column align-items-center">
                    <h2 class="text-center d-inline-block m-auto h4">
                      {{ $t('Invoice:', {code: invoiceToView.code}) }}
                    </h2>
                    <p class="m-0 h6">
                      {{ invoiceToView.created_date | parseDate }}
                    </p>
                  </div>
                  <b-button v-if="invoiceToView.lead_code !== null" variant="outline" size="sm" :to="{name: 'my-lead', params: {code: invoiceToView.lead_code}}">
                    {{ $t('View Lead') }}
                  </b-button>
                </b-col>
              </template>
              <object :data="`${downloadUrl}invoice/${invoiceToView.code}?inline=1`"
                      type="application/pdf" width="100%" height="720"
              >
                alt : <a :href="`${downloadUrl}invoice/${invoiceToView.code}?inline=1`">{{ invoiceToView.code }}</a>
              </object>
            </b-card>
          </transition-expand-side>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TransitionExpandSide from "@/components/animation/TransitionExpandSide";
import MyInvoicesTable from "@/components/table/MyInvoicesTable";
import MyInvoicesMobileList from "@/components/list/MyInvoicesMobileList";
import InvoicePayment from "@/components/form/InvoicePayment";
import PageHeading from "@/components/control/PageHeading";
import InvoicesListControl from '@/components/control/InvoicesListControl';
import userDevice from "@/imports/mixins/userDevice";
import dates from "@/services/date";
import * as DateFns from 'date-fns'
import { captureError } from "@/services/userMonitoring";

export default {
  name: "Invoices",
  components: {MyInvoicesTable, PageHeading, InvoicePayment, MyInvoicesMobileList, InvoicesListControl, TransitionExpandSide},
  mixins: [userDevice],
  data() {
    return {
      pageTitle: this.$t('Invoices'),
      invoiceToView: null,
      sortField: null,
      showExportPane: false,
      exportStart: dates.subtractTime(new Date(), {years: 1}),
      exportEnd: new Date(),
      exportSelection: 'last12',
      minExportDate: dates.subtractTime(new Date(), {years: 7}),
      selectedInvoices: [],
      tableFields: [
        {key:'select', label:this.$t('Select All'), canSort:false, class: 'text-center'},
        {key:'payment_at', label:this.$t('Payment'), canSort:false},
        {key:'created_date', label:this.$t('Date'), canSort:true, customSortLabels: ['Newest', 'Oldest'], sortable:true},
        {key:'code', label: this.$t('Invoice'), canSort:false},
        {key:'description', label:this.$t('Description'), canSort:false},
        {key:'price_including_vat', label:this.$t('Total'), canSort:true, customSortLabels: ['Highest value', 'Lowest value'], sortable:true},
        {key:'pay_now', label:'', canSort:false},
        {key:'pdf', label:'', canSort:false},
        {key:'view', label:'', canSort:false},
      ],
      settleAccount: false,
      queryInvoices: [],
      selectedPane: null,
    }
  },
  computed: {
    isChild: function() {
      return this.$router.currentRoute.name !== 'invoices'
    },
    compactTable: function() {
      return this.selectedPane !== null;
    },
    exportUrl: function() {
      const from = dates.toYMD(this.exportStart);
      const to = dates.toYMD(this.exportEnd);
      return process.env.VUE_APP_DOWNLOAD_URL + `invoice/export-csv?to=${to}&from=${from}`;
    },
    downloadUrl: function() {
      return process.env.VUE_APP_DOWNLOAD_URL
    },
    isMobileDevice: function () {
      return this.isMobile || this.isTablet;
    },
    invoicesContainerToggle: function() {
      return (this.compactTable && !this.isMobile);
    },
    componentRef: function() {
      return this.isMobile ? this.$refs.invoiceList : this.$refs.invoiceTable;
    },
    paneClass: function() {
      return {
        'col-md-8': this.isMobileDevice,
        'm-auto': this.isMobile,
      }
    },
    invoicesContainerClass: function() {
      return {
        'col-md-12': this.invoicesContainerToggle,
        'col-xl-8': this.invoicesContainerToggle,
      }
    },
    accountBalance() {
      return this.$store.state.company.ACCOUNT_BALANCE;
    },
    displaySettleAccount: function() {
      return this.isMobile ? this.accountBalance !== 0 && (this.selectedPane === null) || this.selectedInvoices.length > 0 && (this.selectedPane !== 'payment') : false;
    }
  },
  methods: {
    closeInvoice: function() {
      this.invoiceToView = null;
      this.selectedPane = null;
    },
    onSort: function(value) {
      this.sortField = value;
    },
    onExportCancel: function() {
      this.selectedPane = null;
    },
    onInvoicesSelected: function(invoices) {
      this.selectedInvoices = invoices;
    },
    onPaymentComplete: function() {
      this.settleAccount = false;
      this.componentRef.loadInvoices();
    },
    onSettleAccount: async function() {
      try {
        let result = await this.$api.invoices.listUnpaid();
        let allUnpaidInvoices = result.data.data.map((invoice) => ({...invoice, select: true}));
        this.componentRef.selectUnpaidInvoices(allUnpaidInvoices);
      } catch (e) {
        captureError(e, {
          section: "invoices",
          action: "load-all-unpaid-invoices",
          type: "api-request",
        });
      }
    },
    onPaymentCancel: function() {
      this.selectedPane = null;
      this.componentRef.deselectAll();
    },
    onInvoicesLoaded: function() {
      if (this.settleAccount) {
        this.onSettleAccount();
      } else if (this.queryInvoices.length > 0) {
        this.componentRef.selectInvoices(this.queryInvoices);
      } else if (this.$route.query.code) {
        this.componentRef.viewInvoice(this.$route.query.code);
      }
    },
    onInvoiceRemoved: function(invoice) {
      this.componentRef.deselectInvoice(invoice);
    }
  },
  watch: {
    exportSelection(value) {
      let endDate = DateFns.endOfToday();
      let startDate = DateFns.startOfToday();
      switch (value) {
        case "today":
          this.exportStart = DateFns.startOfToday();
          break;
        case "yesterday":
          startDate = DateFns.startOfYesterday();
          endDate = DateFns.endOfYesterday();
          break;
        case "thisWeek":
          startDate = DateFns.startOfWeek(new Date());
          break;
        case "thisMonth":
          startDate = DateFns.startOfMonth(new Date());
          break;
        case "lastMonth":
          startDate = DateFns.startOfMonth(DateFns.subMonths(new Date(),1));
          endDate = DateFns.endOfMonth(DateFns.subMonths(new Date(),1))
          break;
        case "quarter":
          startDate = DateFns.startOfQuarter(new Date());
          break;
        case "year":
          startDate = DateFns.startOfYear(new Date());
          break;
        case "last12":
          startDate = DateFns.subYears(new Date(), 1);
          break;
      }
      if (value === "all") {
        this.exportStart = this.minExportDate;
      } else {
        this.exportStart = startDate;
      }
      this.exportEnd = endDate;
    },
    selectedInvoices: function() {
      if (this.selectedInvoices.length > 0 && !this.isMobile) {
        this.selectedPane = 'payment';
      }
    },
    invoiceToView: function(value) {
      this.selectedPane = (value !== null) ? 'invoice' : null;
    }
  },
  mounted() {
    this.queryInvoices = Array.isArray(this.$route.query.selectedInvoice) ? this.$route.query.selectedInvoice
      : (this.$route.query.selectedInvoice !== undefined ? [this.$route.query.selectedInvoice] : []);
    this.settleAccount = (this.$route.query.settleAccount !== undefined);
  }
}
</script>
<style lang="scss">
@media only screen and (min-width: 768px) and (max-width: 1540px) {
  .invoices-container {
    .sidebar-backdrop {
      opacity: 1;
      background: rgba(1, 54, 82, .2);
      top: 0;
      left: 0;
      z-index: 3;
      width: 100vw;
      height: 100vh;
    }

    .sidebar {
      position: fixed !important;
      width: 230px;
      float: left;
      top: 71px;
      height: calc(100% - 60px);
      z-index: 11;
      left: 0;

      &-panel {
        border-radius: 0;
        box-shadow: $box-shadow-secondary;
      }
    }
  }
}
</style>
<style scoped lang="scss">
.close {
  z-index: 1;
}

.fake-select {
  outline: none;
  width: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 5px 10px;
  margin-left: 1rem;
  .btn {
    min-width: 125px;
  }
}

.filter {
  .separator {
    &:last-child:before {
      content: none;
    }
  }

  &-item {
    outline: none;
    width: auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 5px 10px;
  }
}

.separator {
  position: relative;

  &:before {
    width: 1px;
    background: $border-color;
    position: absolute;
    content: "\200C";
    height: 54%;
    right: -8px;
    top: 23%;
  }

  &-icons {
    &:before {
      right: -15px;
    }
  }
}

.sticky-basket {
  top: 90px;
  position: sticky;
}

@include media-breakpoint-down(lg) {
  .settle-account {
    top: 80px;
    z-index: 2;
  }
}

@media only screen and (max-width: 1220px) {
  .settle-invoice {
    width: 120px;
  }
}

@include media-breakpoint-down(lg) {
  .separator {
    text-align: center;
    margin: 10px auto 0;
    &:before {
      width: 0;
    }
  }
}

@media only screen and (max-width: 1450px) {
  ::v-deep .list-control__desktop {
    .filter {
      max-width: 410px;
      flex-wrap: wrap;
      .fake-select {
        flex-grow: 1;
        flex-basis: 45%;
        .btn {
          width: 100%;
        }
      }
      .manage-payment {
        order: 2;
      }
      .settle-account {
        order: 0
      }
      .export-invoices {
        order: 1;
        &:before {
          width: 0;
        }
      }
      .sort-by {
        order: 3
      }
    }
  }
}

.btn-settle-account {
  font-size: unset;
}

@media only screen and (max-width: 1539px) {
  .dynamic-container {
    .page-container {
      width: 100%
    }
  }
}

@media only screen and (max-width: 374px) {
  .account-balance {
    flex-direction: column !important;

    &-details {
      flex: 0 0 100%;
      max-width: 100%;
      margin-bottom: 10px;
    }
  }
}
</style>
